import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
} from '@mui/material'
import { useEffect } from 'react'
import { useLazyPrintLocalPQuery } from '@/store/api/localPApi'
import { useSnackbar } from '@/hooks/useSnackbar'
import { printAndUploadReport } from '@/utils/printAndUploadReport'
import { HIDE_ON_MOBILE_INLINE, PRINT_PREVIEW_DIALOG_PAPER, TEXT_TRUNCATE } from '@/theme/responsive'

// Access port: rpProcurementLiquid ("BIÊN BẢN THANH LÝ HỢP ĐỒNG" = contract
// liquidation minutes) for the Purchase tab of /purchase/control. Self-fetching
// dialog: lazy-fires the print query on open, renders returned `html` into an
// iframe srcDoc preview + a Print button. Mirrors DoPrintDialog.
//
// kind 'liquid' is added to LocalPPrintKind + BE by the orchestrator.

interface Props {
  open: boolean
  contractNo: string
  onClose: () => void
}

// Override CSS preview to fit A4 in scaled iframe (memory: print fit A4).
// Same override set as DoPrintDialog (covers both flat-flow + .ctl absolute).
const PRINT_PREVIEW_CSS = `
@page { size: A4 !important; margin: 0 !important; }
@media print {
  @page { size: A4 !important; margin: 0 !important; }
  body { margin: 0 !important; }
  .report-page { width: 210mm !important; max-width: none !important; min-height: 297mm !important; padding: 9mm 11mm !important; }
}
@media screen and (max-width: 600px) {
  .report-page { min-height: auto !important; max-width: 100% !important; width: 100% !important; padding: 4mm !important; margin: 0 !important; }
  body { margin: 0 !important; background: #fff !important; }
}
body { margin: 0 !important; }
.print-action { display: none !important; }
.report-page { width: 100% !important; max-width: 170mm !important; padding: 0 !important; min-height: auto !important; margin: 0 auto !important; }
`.trim()

/** Inject base href + PRINT_PREVIEW_CSS override + strip inline print button. */
function buildPreviewHtml(rawHtml: string): string {
  const withoutInlinePrint = rawHtml.replace(/<button\s+class="print-action[^>]*>[\s\S]*?<\/button>/g, '')
  const withBase = withoutInlinePrint.replace('<head>', `<head><base href="${window.location.origin}/">`)
  const styleTag = `<style id="liquid-print-preview-overrides">${PRINT_PREVIEW_CSS}</style>`
  if (withBase.includes('</head>')) return withBase.replace('</head>', `${styleTag}</head>`)
  return `${styleTag}${withBase}`
}

function sanitizePdfFileNamePart(value: string): string {
  return value.trim().replace(/[\\/:*?"<>|]+/g, '-').replace(/\s+/g, '_')
}

export default function ProcurementLiquidPrintDialog({ open, contractNo, onClose }: Props) {
  const snack = useSnackbar() // snackbar chuẩn app (góc dưới-phải), thay Snackbar top-center
  // currentData (KHÔNG dùng `data`): `data` giữ kết quả thành công gần nhất kể cả
  // khi request hiện tại lỗi → contract không có invoice sẽ mở lại preview CŨ của
  // contract in trước đó (bug "vừa alert vừa mở form"). currentData = undefined khi
  // lỗi/đổi arg → preview chỉ hiện đúng contract hiện tại.
  const [trigger, { currentData, isFetching, isError, error }] = useLazyPrintLocalPQuery()

  useEffect(() => {
    if (open && contractNo) {
      void trigger({ contractNo, kind: 'liquid' })
    }
  }, [open, contractNo, trigger])

  const rawHtml = currentData?.data?.html ?? ''
  const previewHtml = rawHtml ? buildPreviewHtml(rawHtml) : ''
  const iframeTitle = 'Inbound Logistics Liquid Print Preview'

  // Surface BE ValueError ("This contract is no invoice!") as a toast.
  useEffect(() => {
    if (open && isError) {
      const detail =
        (error as { data?: { detail?: string } } | undefined)?.data?.detail ??
        'Cannot load liquidation minutes.'
      snack.error(detail)
      onClose() // đóng lại — tránh dialog "armed" tự mở khi contract prop đổi sau đó
    }
  }, [open, isError, error, onClose, snack])

  return (
    <>
      {open && isFetching && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 1300,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(0,0,0,0.35)',
          }}
        >
          <CircularProgress size={48} />
        </Box>
      )}
      <Dialog
        open={open && Boolean(rawHtml)}
        onClose={onClose}
        maxWidth={false}
        fullWidth
        PaperProps={{ sx: PRINT_PREVIEW_DIALOG_PAPER }}
      >
        <DialogTitle
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, py: 1 }}
        >
          <Box
            component="span"
            title={`Print Preview - Liquid (${contractNo})`}
            sx={{ ...TEXT_TRUNCATE, flex: '1 1 auto', minWidth: 0, fontWeight: 700, fontSize: '1rem' }}
          >
            <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>
              Print Preview -{' '}
            </Box>
            Liquid ({contractNo})
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={onClose} size="small">
              Cancel
            </Button>
            <Button
              variant="contained"
              size="small"
              disabled={!rawHtml}
              onClick={() => {
                const printTitle = `${sanitizePdfFileNamePart(contractNo || 'procurement_liquid')}_Liquid.pdf`
                void printAndUploadReport({
                  iframeTitle,
                  reportName: 'rpProcurementLiquid',
                  printTitle,
                  html: previewHtml,
                  onUploadError: (m) => snack.error(m),
                })
              }}
            >
              Print
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ height: '78vh', p: 0 }}>
          <Box
            component="iframe"
            title={iframeTitle}
            srcDoc={previewHtml}
            sx={{ width: '100%', height: '100%', border: 0, display: 'block', bgcolor: 'white' }}
          />
        </DialogContent>
      </Dialog>
    </>
  )
}
